<script lang="ts">
  import { onMount } from "svelte";
  import axios from "axios";
  import Admissions from "./Admissions.svelte";
  import Locations from "./Locations.svelte";
  import Timeslot from "./Timeslot.svelte";
  import TypeWrapper from "./TypeWrapper.svelte";
  import Payment from "./Payment.svelte";
  import Confirmation from "./Confirmation.svelte";
  import HeaderSteps from "./HeaderSteps.svelte";
  import VideoSection from "./VideoSection.svelte";

  import { book, parentElement, step } from "../../stores/WalkinBookingStore";
  import { hasGroupon } from "../../stores/BookingStore";

  let locations: any = [];
  let admissions: any = null;
  let timeslots: any = null;
  let selectedType: string = "throw-em-all";
  let selectedStep: number = 1;

  let selectedLocation: string = "";
  let selectedAdmission: string = "1";
  let selectedTimeslot: string = "";
  let bookingLength: number = 60;

  let localData: any = {};
  let previousLocation: string = "";
  let previousAdmission: string | null = null;
  let paymentProcessed: boolean = false;

  let locationParam: string = "";

  step.subscribe((value) => {
    selectedStep = value;
  });

  // Initialize store subscription
  book.subscribe((value) => {
    localData = value;
    // Ensure admission is always handled as a string
    selectedAdmission = value.admission ? String(value.admission) : "";
    selectedTimeslot = value.timeslot || "";
    selectedLocation = value.location;
    bookingLength = parseInt(value.duration);
    paymentProcessed = value.paymentProcessed;

    // Handle admission changes through store updates
    const currentAdmission = value.admission ? String(value.admission) : "";
    if (currentAdmission && currentAdmission !== previousAdmission) {
      previousAdmission = currentAdmission;

      // Reset timeslots
      selectedTimeslot = "";
      timeslots = null;
    }
  });

  onMount(() => {
    // get the locations list
    axios.get(`/wp-json/badaxe/v1/location/list`).then((response: any) => {
      locations = response.data;

      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has("l")) {
        locationParam = urlParams.get("l") as string;
        locationParam = atob(locationParam);
      }

      locations.forEach((l: any) => {
        if (locationParam == l.location) {
          selectedLocation = String(l.id); // Ensure id is a string
          book.update((value) => ({
            ...value,
            location: selectedLocation,
            locationName: l.name,
            locationCountry: l.country,
          }));
        }
      });
    });
  });

  // Watch for location changes
  $: if (selectedLocation && locations.length > 0) {
    const normalizedLocation = String(selectedLocation);
    const normalizedStoreLocation = localData?.location
      ? String(localData.location)
      : "";

    // Only proceed if the location has actually changed or hasn't been processed yet
    if (
      normalizedLocation !== normalizedStoreLocation ||
      normalizedLocation !== previousLocation
    ) {
      previousLocation = normalizedLocation; // Track that we've processed this location

      // Reset all dependent data first
      selectedAdmission = "";
      selectedTimeslot = "";
      admissions = null;
      timeslots = null;

      // Find the selected location data
      const locationData = locations?.find(
        (loc: any) => String(loc.id) === normalizedLocation,
      );

      // Update store with new location and reset dependent data
      book.update((value) => ({
        ...value,
        location: normalizedLocation,
        locationName: locationData?.name || "",
        locationCountry: locationData?.country || "",
        admission: "",
        timeslots: [],
        admissions: [],
        duration: 60,
        type: "throw-em-all",
      }));

      // Fetch new data for the selected location
      fetchAdmissions(normalizedLocation);
    }
  }

  // Watch for timeslot changes
  $: if (selectedTimeslot) {
    const normalizedTimeslot = String(selectedTimeslot);
    const normalizedStoreTimeslot = localData?.timeslot
      ? String(localData.timeslot)
      : "";

    if (normalizedTimeslot !== normalizedStoreTimeslot) {
      book.update((value) => ({
        ...value,
        timeslot: normalizedTimeslot,
      }));
    }
  }

  // Watch for type changes
  $: if (selectedType) {
    if (selectedLocation !== "" && selectedAdmission != "") {
      axios
        .get(
          `/wp-json/badaxe/v1/location/${selectedLocation}/${selectedAdmission}/${bookingLength}/walkin-timeslots`,
        )
        .then((response: any) => {
          book.update((obj) => ({
            ...obj,
            timeslots: response.data,
          }));
        })
        .catch((error) => {
          console.error("Error fetching timeslots:", error);
          // timeslots = [];
          book.update((obj) => ({
            ...obj,
            timeslots: [],
          }));
        });
      // if (normalizedType !== normalizedStoreType) {
      //   book.update((value) => ({
      //     ...value,
      //     type: normalizedType,
      //     duration:
      //       normalizedType === "first"
      //         ? 30
      //         : normalizedType === "second"
      //           ? 60
      //           : 0,
      //   }));
      // }
    }
  }

  const fetchAdmissions = async (locationId: string) => {
    const normalizedLocationId = String(locationId);
    if (!normalizedLocationId) return;

    try {
      const response = await axios.get(
        `/wp-json/badaxe/v1/location/${normalizedLocationId}/admissions/list`,
      );
      // Ensure admissions are strings
      admissions = response.data.map(String);
      previousLocation = normalizedLocationId;
      book.update((value) => ({
        ...value,
        admissions: admissions,
      }));
    } catch (error) {
      console.error("Error fetching admissions:", error);
      admissions = [];
      book.update((value) => ({
        ...value,
        admissions: [],
      }));
    }
  };
</script>

<div
  class="h-auto flex flex-col items-center justify-start gap-4 border-2 border-gray-400 rounded-lg w-full sm:max-w-[450px] mx-auto px-8 sm:px-8 py-8 sm:py-8 mt-16 mb-16"
>
  {#if selectedStep < 5}
    <HeaderSteps />
  {/if}
  {#if !paymentProcessed}
    {#if selectedStep === 1}
      <Locations {locations} bind:location={selectedLocation} />
    {/if}

    {#if selectedStep === 2}
      <Admissions {admissions} bind:selectedAdmission />
    {/if}

    <!-- {#if selectedStep === 3}
      <TypeWrapper
        bind:selectedType
        duration={selectedType === "hatchet-or-knife" ? 30 : 60}
      />
    {/if} -->

    {#if selectedStep === 3}
      <Timeslot {timeslots} bind:selectedTimeslot />
    {/if}

    {#if selectedStep === 4}
      <Payment parentEl={`walkin-booking`} />
    {/if}
  {:else}
    {#if selectedStep === 5}
      <VideoSection />
    {/if}
    {#if selectedStep === 6}
      <Confirmation />
    {/if}
  {/if}
</div>

<style></style>
